import { List } from 'immutable'
import React from 'react'
import { any, fromJSX, Updater, Widget } from 'widgets-for-react'
import { BlocksLayout } from './blocksLayout'
import { AdditionalBlockData, Block, blocksStateUpdaters } from './blocksState'
import { bmiBlockWidget } from './block/bmiBlock/bmiBlockWidget'
import { CtaBlockLayout } from './block/ctaBlockLayout'
import { expandBlockWidget } from './block/expandBlock/expandBlockWidget'
import { LinkGridCentraBlockLayout } from './block/linkGridCentraBlockLayout'
import { locationExpandableBlockWidget } from './block/locationExpandbleBlok/locationExpandableBlockWidget'
import { newsTeasersBlockWidget } from './block/newsTeasersBlock/newsTeasersBlockWidget'
import { peopleBlockWidget } from './block/peopleBlock/peopleBlockWidget'
import { phoneNumbersBlockWidget } from './block/phoneNumbersBlock/phoneNumbersBlockWidget'
import { relatedContentBlockWidget } from './block/relatedContentBlock/relatedContentBlockWidget'
import { specialistsBlockWidget } from './block/specialistsBlock/specialistsBlockWidget'
import { waitingTimesBlockWidget } from './block/waitingTimesBlock/waitingTimesBlockWidget'
import { FeaturedContentBlockLayout } from './block/featuredContentBlockLayout'
import { LinkBlockLayout } from './block/linkBlockLayout'
import { LinkGridBlockLayout } from './block/linkGridBlockLayout'
import { CarouselBlockLayout } from './block/carouselBlockLayout'
import { LocationBlockLayout } from './block/locationBlockLayout'
import { MediaBlockLayout } from './block/mediaBlockLayout'
import { QuoteBlockLayout } from './block/quoteBlockLayout'
import { TextBlockLayout } from './block/textBlockLayout'
import { TextImageBlockLayout } from './block/textImageBlockLayout'
import { initialLocationExpandableBlockState } from './block/locationExpandbleBlok/locationExpandableBlockState'
import { jotFormsBlockWidget } from './block/jotFormsBlock/jotform.widget'
import { getCookie } from '../../../utils/cookies'
import { DossierHightlightBlockWidget } from './block/dossierHighlightBlock/dossierHighlightBlock.widget'

export const blocksWidget =
  (acceptVideoCookie: () => void, additionalBlockData?: AdditionalBlockData) => (blocks: List<Block>) =>
    any(
      blocks
        .map((block) =>
          blockSwitch(
            acceptVideoCookie,
            additionalBlockData ?? {}
          )(block).map(blocksStateUpdaters.blockUpdater(block.index))
        )
        .toArray()
    ).wrapHTML((element) => <BlocksLayout children={element} />)

const blockSwitch =
  (acceptVideoCookie: () => void, additionalBlockData: AdditionalBlockData) =>
  (block: Block): Widget<Updater<Block>> => {
    const departmentOrCentrumTitle =
      additionalBlockData.department?.title ??
      additionalBlockData.subdepartment?.title ??
      additionalBlockData.centrum?.title ??
      additionalBlockData.subcentrum?.title

    const videoCookieAccepted = getCookie("video-cookies") == 'yes'

    switch (block.kind) {
      case 'ctaBlock':
        return fromJSX(() => <CtaBlockLayout {...block} />)
      case 'expandBlock':
        return expandBlockWidget(block, acceptVideoCookie).map(blocksStateUpdaters.expandBlockStateUpdater)
      case 'expandBlockWithImage':
        return expandBlockWidget(block, acceptVideoCookie).map(blocksStateUpdaters.expandBlockStateUpdater)
      case 'featuredContentBlock':
        return fromJSX(() => <FeaturedContentBlockLayout {...block} />)
      case 'linkBlock':
        return fromJSX(() => <LinkBlockLayout {...block} />)
      case 'carouselBlock':
        return fromJSX(() => <CarouselBlockLayout {...block} />)
      case 'linkGridBlock':
        return fromJSX(() => <LinkGridBlockLayout {...block} />)
      case 'linkGridCentraBlock':
        return fromJSX(() => <LinkGridCentraBlockLayout {...block} />)
      case 'mediaBlock':
        return fromJSX(() => <MediaBlockLayout {...block} videoCookieAccepted={videoCookieAccepted} acceptVideoCookie={acceptVideoCookie} />)
      case 'quoteBlock':
        return fromJSX(() => <QuoteBlockLayout {...block} />)
      case 'textImageBlock':
        return fromJSX(() => <TextImageBlockLayout {...block} />)
      case 'textBlock':
        return fromJSX(() => <TextBlockLayout {...block} />)
      case 'peopleBlock':
        return peopleBlockWidget(block).map(blocksStateUpdaters.peopleBlockStateUpdater)
      case 'specialistsBlock':
        return specialistsBlockWidget(block, departmentOrCentrumTitle).map(
          blocksStateUpdaters.specialistsBlockStateUpdater
        )
      case 'newsTeasersBlock':
        return newsTeasersBlockWidget(block).map(blocksStateUpdaters.newsTeasersBlockStateUpdater)
      case 'phoneNumbersBlock':
        return phoneNumbersBlockWidget(block).map(blocksStateUpdaters.phoneNumbersBlockStateUpdater)
      case 'relatedContentBlock':
        return relatedContentBlockWidget(block).map(blocksStateUpdaters.relatedContentBlockStateUpdater)
      case 'locationBlock':
        return fromJSX(() => (
          <LocationBlockLayout
            {...block}
            location={
              additionalBlockData.location && additionalBlockData.location.id === block.locationId
                ? additionalBlockData.location
                : undefined
            }
          />
        ))
      case 'bmiBlock':
        return bmiBlockWidget(block).map(blocksStateUpdaters.bmiBlockStateUpdater)
      case 'locationExpandableBlock':
        const actualBlock =
          block.locationExpandableBlockState.kind != 'unloaded'
            ? block
            : additionalBlockData.subdepartment
            ? {
                ...block,
                locationExpandableBlockState: initialLocationExpandableBlockState(
                  'subdepartment',
                  additionalBlockData.subdepartment.id
                ),
              }
            : additionalBlockData.centrum
            ? {
                ...block,
                locationExpandableBlockState: initialLocationExpandableBlockState(
                  'centrum',
                  additionalBlockData.centrum.id
                ),
              }
            : additionalBlockData.subcentrum
            ? {
                ...block,
                locationExpandableBlockState: initialLocationExpandableBlockState(
                  'subcentrum',
                  additionalBlockData.subcentrum.id
                ),
              }
            : block

        return locationExpandableBlockWidget(actualBlock).map(blocksStateUpdaters.locationExpandableBlockStateUpdater)
      case 'jotFormBlock':
        return jotFormsBlockWidget(block).map(blocksStateUpdaters.jotFormsBlockStateUpdater)
      case 'waitingTimesBlock':
        if (departmentOrCentrumTitle) {
          const forKind = additionalBlockData.department ? 'department' : 'centrum'
          return waitingTimesBlockWidget(block, { forPage: forKind, title: departmentOrCentrumTitle }).map(
            blocksStateUpdaters.waitingTimesBlockStateUpdater
          )
        } else {
          return waitingTimesBlockWidget(block).map(blocksStateUpdaters.waitingTimesBlockStateUpdater)
        }
      case 'dossierBlock':
        return DossierHightlightBlockWidget(block).map(blocksStateUpdaters.dossierBlockStateUpdater)
      default:
        return fromJSX<never>(() => <></>)
    }
  }
